import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Crown, Leaf, Baby, Sparkles, Star, ArrowRight, Plus, Fish, UtensilsCrossed } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/zkwjbg2h_Royal%20Delights%20Logo.png";
const SALMON_IMG = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/pvrclu49_RoyalDragon_HibachiSalmon.jpg";
const SHRIMP_SKEWERS_IMG = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/rq0obo8x_RoyalDragon_ShripSkewers.jpg";
const HIBACHI_STEAK_IMG = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/iftkz5tu_RoyalDragon_HibachiSteak.jpg";
const HIBACHI_SHRIMP_IMG = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/nb10gqiy_RoyalDragon_HibachiShrimp.jpg";
const HIBACHI_CHICKEN_IMG = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/jtbzavwk_RoyalDragon_HibachiChicken.jpg";
const CAULIFLOWER_RICE_IMG = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/ukhpgooa_Keto-Cauliflower-Fried-Rice-square.jpg";
const LOBSTER_IMG = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/uyehacwk_broiled_lobster-500x500.jpg";
// New premium plated images
const STEAK_LOBSTER_PLATE = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/6xq6raql_copilot_image_1770938235981.jpeg";
const STEAK_SHRIMP_PLATE = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/7ohomzqa_image_1770937982622.jpeg";
const BENTO_SPREAD = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/jt37pe3s_image_1770939759278.jpeg";
const COMBO_PLATE = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/u77mu1si_copilot_image_1770940164719.jpeg";
const HERO_IMAGE = "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxjaGVmJTIwcGxhdGluZyUyMGdvdXJtZXQlMjBmb29kJTIwY2xvc2UlMjB1cCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3MDg2NTMyNnww&ixlib=rb-4.1.0&q=85";

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

export const MenuPage = () => {
  // ==================== PLATED DINNER MENU ====================
  const platedMenu = [
    { name: "American Wagyu NY Strip", subtitle: "The Royal Reserve", price: 85, description: "Rich marbling, soulful garlic-ginger butter, refined soy reduction.", image: STEAK_SHRIMP_PLATE },
    { name: "Filet & Salmon", subtitle: "The Emperor's Surf & Turf", price: 75, description: "Tender filet, citrus-glazed salmon, herb-infused butter.", image: COMBO_PLATE },
    { name: "Steak & Shrimp", subtitle: "The Royal Duo", price: 70, description: "Signature seasoning, soulful butter, refined hibachi finish.", image: STEAK_SHRIMP_PLATE },
    { name: "A5 Japanese Wagyu", subtitle: "The Crown Jewel", price: 125, description: "Ultra-premium Wagyu with a delicate soy-citrus glaze.", featured: true, image: STEAK_LOBSTER_PLATE },
    { name: "Salmon & Warm Water Lobster", subtitle: "The King's Surf Feast", price: 90, description: "Buttery lobster tail, seared salmon, lemon-ginger butter.", image: STEAK_LOBSTER_PLATE },
    { name: "Shrimp & Scallops", subtitle: "The Ocean Court", price: 75, description: "Sweet scallops, tender shrimp, soulful butter.", image: HIBACHI_SHRIMP_IMG },
    { name: "Chicken & Shrimp", subtitle: "The Family Favorite", price: 65, description: "Comforting, flavorful, elevated.", image: HIBACHI_CHICKEN_IMG }
  ];

  // ==================== BUFFET MENU ====================
  const buffetMenu = [
    { name: "Original Buffet", subtitle: "The People's Feast", price: 55, description: "NY Strip Steak, Shrimp, Chicken." },
    { name: "Splash Buffet", subtitle: "The Ocean Royalty Feast", price: 75, description: "Shrimp, Scallops, Salmon, Warm Water Lobster." },
    { name: "Royal Buffet", subtitle: "The Grand Royal Banquet", price: 85, description: "Steak, Salmon, Shrimp, Chicken, Lobster.", featured: true },
    { name: "The Pick 3", subtitle: "The Royal Trio", price: 65, description: "Choose any three proteins." },
    { name: "The Royal Delight", subtitle: "The Monarch's Choice", price: 100, description: "Choose any five proteins." }
  ];

  // ==================== EXPANDED ENTRÉE COMBINATIONS ====================
  const entreeCombos = [
    { name: "Pick 3", subtitle: "Your Choice", price: 80, description: "Choose any three proteins." },
    { name: "The Emperor's Feast", subtitle: "Premium Selection", price: 95, description: "Filet, Lobster, Scallops.", featured: true },
    { name: "The Soulful Surf", subtitle: "Ocean's Best", price: 85, description: "Salmon, Shrimp, Scallops." },
    { name: "The King's Cut", subtitle: "Royal Proteins", price: 90, description: "Wagyu, Chicken, Shrimp." },
    { name: "The Monarch's Mix", subtitle: "Ultimate Experience", price: 100, description: "Steak, Salmon, Shrimp, Chicken, Lobster." }
  ];

  // ==================== SUSHI & SASHIMI - THE ROYAL ROLL ROOM ====================
  const signatureRolls = [
    { name: "The Emperor's Flame", subtitle: "Signature Roll", price: 18, description: "Spicy tuna, avocado, cucumber, topped with seared salmon, spicy mayo, and gold flakes." },
    { name: "The Garden Crown", subtitle: "Vegan Roll", price: 14, description: "Avocado, cucumber, pickled radish, mango, sesame glaze.", vegan: true },
    { name: "The Royal Reserve", subtitle: "Luxury Roll", price: 22, description: "Lobster tempura, cream cheese, scallion, topped with torched wagyu slices.", featured: true },
    { name: "The Soul Roll", subtitle: "House Favorite", price: 17, description: "Crab, shrimp tempura, spicy aioli, jalapeño, eel sauce." },
    { name: "The King's Touch", subtitle: "Fresh & Light", price: 16, description: "Salmon, avocado, asparagus, lemon zest, yuzu glaze." },
    { name: "The Crown Jewel", subtitle: "Premium Roll", price: 25, description: "A5 Wagyu, scallion, garlic soy butter, wrapped in soy paper.", featured: true }
  ];

  const sashimiSelections = [
    { name: "Royal Salmon", subtitle: "5 Slices", price: 18, description: "Citrus-cured salmon with microgreens." },
    { name: "Imperial Tuna", subtitle: "5 Slices", price: 20, description: "Sashimi-grade tuna with yuzu pearls." },
    { name: "Wagyu Sashimi", subtitle: "5 Slices", price: 28, description: "A5 Wagyu with garlic soy drizzle.", featured: true },
    { name: "The Ocean Court", subtitle: "Chef's Choice", price: 30, description: "Chef's choice of 3 fish, plated with edible flowers." }
  ];

  const sushiAddOns = [
    { name: "Miso Soup", price: "$5", description: "Traditional Japanese soup" },
    { name: "Seaweed Salad", price: "$6", description: "Fresh and tangy" },
    { name: "Pickled Ginger & Wasabi", price: "Included", description: "Complimentary" },
    { name: "Sushi Setup Fee", price: "$150", description: "Table, tools, chef station" },
    { name: "Sushi Roll Sampler", price: "$45", description: "3 signature rolls" }
  ];

  // ==================== VEGAN MENU ====================
  const veganMenu = [
    { name: "Teriyaki Plant-Based Chicken", subtitle: "The Garden Royal", price: 25, description: "Savory plant-based protein with our signature teriyaki glaze." },
    { name: "Vegetable Spring Rolls", subtitle: "The Royal Crisp", price: 10, description: "Crispy, golden vegetable rolls with sweet chili dip." },
    { name: "Cauliflower Fried Rice", subtitle: "The Emerald Garden Rice", price: 15, description: "Low-carb, full flavor alternative with fresh vegetables.", image: CAULIFLOWER_RICE_IMG },
    { name: "The Garden Crown Roll", subtitle: "Vegan Sushi", price: 14, description: "Avocado, cucumber, pickled radish, mango, sesame glaze." }
  ];

  // ==================== STARTERS & ADD-ONS ====================
  const startersAndSides = [
    { name: "Hibachi Ribs", price: "$20", description: "Slow-cooked, hibachi-finished ribs with soulful glaze" },
    { name: "Spring Rolls", price: "$10", description: "Crispy vegetable rolls with signature dipping sauce" },
    { name: "Yakisoba Noodles", price: "$10", description: "Stir-fried noodles with vegetables and savory hibachi sauce" },
    { name: "Royal Sushi Add-On", price: "$150", description: "Live sushi station added to any hibachi experience" }
  ];

  const eventAddOns = [
    { name: "Full Event Setup", price: "$20/person", description: "Complete setup and styling" },
    { name: "Saxophonist", price: "$350", description: "Live entertainment" },
    { name: "Additional Server", price: "$125", description: "Per server" }
  ];

  // ==================== POLICIES ====================
  const policies = [
    "$850 minimum invoice",
    "Travel beyond 45 miles: $1.25 per mile",
    "Allergies must be communicated",
    "All hibachi meals include fried rice + vegetables",
    "Kids under 7 eat free",
    "Buffets require 10+ guests"
  ];

  // ==================== MENU ITEM COMPONENT ====================
  const MenuItem = ({ item, index, showImage = false }) => (
    <motion.div
      variants={fadeInUp}
      className={`group relative ${item.featured ? 'col-span-full' : ''}`}
      data-testid={`menu-item-${index}`}
    >
      <div className={`bg-[#121212] border border-white/5 rounded-2xl overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-500 ${item.featured ? 'p-0' : 'p-6'}`}>
        {item.featured ? (
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="aspect-video lg:aspect-auto relative overflow-hidden">
              <img 
                src={item.image || STEAK_LOBSTER_PLATE} 
                alt={item.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#121212]/80 lg:block hidden" />
            </div>
            <div className="p-8 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-xs uppercase tracking-wider font-body">Signature</span>
              </div>
              <h3 className="font-heading text-2xl lg:text-3xl font-bold text-[#F5F5F0] mb-2">{item.name}</h3>
              <p className="font-accent text-[#D4AF37] italic mb-4">{item.subtitle}</p>
              <p className="font-body text-[#A1A1AA] mb-6">{item.description}</p>
              <span className="font-heading text-3xl text-[#D4AF37]">${item.price}</span>
            </div>
          </div>
        ) : (
          <>
            {showImage && item.image && (
              <div className="aspect-video rounded-xl overflow-hidden mb-4">
                <img 
                  src={item.image} 
                  alt={item.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            )}
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-xl font-semibold text-[#F5F5F0] group-hover:text-[#D4AF37] transition-colors">{item.name}</h3>
                  {item.vegan && <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">V</span>}
                </div>
                <p className="font-accent text-sm text-[#D4AF37] italic mb-2">{item.subtitle}</p>
                {item.description && <p className="font-body text-sm text-[#A1A1AA]">{item.description}</p>}
              </div>
              <span className="font-heading text-2xl text-[#D4AF37] flex-shrink-0">${item.price}</span>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );

  return (
    <div data-testid="menu-page" className="min-h-screen bg-[#050505]">
      
      {/* ==================== HERO ==================== */}
      <section className="relative py-32 lg:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE} 
            alt="Gourmet food plating" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/80 via-[#050505]/60 to-[#050505]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p variants={fadeInUp} className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4 font-body">
              Our Menu
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-[#F5F5F0] mb-6">
              Soulful <span className="italic text-[#D4AF37]">Luxury</span> Cuisine
            </motion.h1>
            <motion.p variants={fadeInUp} className="font-body text-lg text-[#A1A1AA]">
              From sizzling hibachi to artful sushi — each dish blends deep, comforting seasoning with refined execution.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ==================== FOOD SHOWCASE ==================== */}
      <section className="py-12 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
            {[STEAK_LOBSTER_PLATE, STEAK_SHRIMP_PLATE, COMBO_PLATE, BENTO_SPREAD, HIBACHI_STEAK_IMG, SALMON_IMG, LOBSTER_IMG, HIBACHI_CHICKEN_IMG].map((img, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="aspect-square rounded-2xl overflow-hidden"
              >
                <img src={img} alt="Signature dish" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== MENU TABS ==================== */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Tabs defaultValue="plated" className="w-full">
            <TabsList className="w-full flex flex-wrap justify-center bg-transparent gap-2 mb-16">
              {[
                { value: "plated", label: "Plated Dinner", icon: <Crown className="w-4 h-4" /> },
                { value: "buffet", label: "Buffet", icon: <Sparkles className="w-4 h-4" /> },
                { value: "combos", label: "Combos", icon: <UtensilsCrossed className="w-4 h-4" /> },
                { value: "sushi", label: "Sushi & Sashimi", icon: <Fish className="w-4 h-4" /> },
                { value: "vegan", label: "Vegan", icon: <Leaf className="w-4 h-4" /> },
                { value: "kids", label: "Kids", icon: <Baby className="w-4 h-4" /> },
                { value: "addons", label: "Add-Ons", icon: <Plus className="w-4 h-4" /> }
              ].map((tab) => (
                <TabsTrigger 
                  key={tab.value}
                  value={tab.value} 
                  data-testid={`tab-${tab.value}`}
                  className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-[#A1A1AA] font-body uppercase tracking-wider px-4 sm:px-6 py-3 rounded-full border border-white/10 data-[state=active]:border-[#D4AF37] transition-all duration-300 flex items-center gap-2 text-xs sm:text-sm"
                >
                  {tab.icon}
                  <span className="hidden sm:inline">{tab.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {/* ==================== PLATED DINNER TAB ==================== */}
            <TabsContent value="plated">
              <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
                <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-10 gap-4">
                  <div>
                    <h2 className="font-heading text-3xl lg:text-4xl font-bold text-[#F5F5F0] flex items-center gap-3">
                      <Crown className="w-8 h-8 text-[#D4AF37]" />
                      The Royal Dinner Party
                    </h2>
                    <p className="font-body text-[#A1A1AA] mt-2">Plated Experience | 2–12 Guests | $65–$125 per person</p>
                  </div>
                  <p className="font-body text-sm text-[#D4AF37] bg-[#D4AF37]/10 px-4 py-2 rounded-full">
                    Includes a dedicated server
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {platedMenu.map((item, index) => (
                    <MenuItem key={index} item={item} index={index} showImage={index < 2} />
                  ))}
                </div>
              </motion.div>
            </TabsContent>

            {/* ==================== BUFFET TAB ==================== */}
            <TabsContent value="buffet">
              <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
                <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-10 gap-4">
                  <div>
                    <h2 className="font-heading text-3xl lg:text-4xl font-bold text-[#F5F5F0] flex items-center gap-3">
                      <Sparkles className="w-8 h-8 text-[#D4AF37]" />
                      The Royal Feast Buffet
                    </h2>
                    <p className="font-body text-[#A1A1AA] mt-2">Buffet Experience | 10–40 Guests | $55–$100 per person</p>
                  </div>
                  <p className="font-body text-sm text-[#D4AF37] bg-[#D4AF37]/10 px-4 py-2 rounded-full">
                    Staffing billed separately
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {buffetMenu.map((item, index) => (
                    <MenuItem key={index} item={item} index={index} />
                  ))}
                </div>
              </motion.div>
            </TabsContent>

            {/* ==================== COMBOS TAB ==================== */}
            <TabsContent value="combos">
              <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
                <div className="mb-10">
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold text-[#F5F5F0] flex items-center gap-3">
                    <UtensilsCrossed className="w-8 h-8 text-[#D4AF37]" />
                    Entrée Combinations
                  </h2>
                  <p className="font-body text-[#A1A1AA] mt-2">Premium protein combinations for the ultimate experience</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {entreeCombos.map((item, index) => (
                    <MenuItem key={index} item={item} index={index} />
                  ))}
                </div>
              </motion.div>
            </TabsContent>

            {/* ==================== SUSHI & SASHIMI TAB ==================== */}
            <TabsContent value="sushi">
              <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
                {/* Section Header */}
                <div className="mb-12 text-center max-w-3xl mx-auto">
                  <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 px-4 py-2 rounded-full mb-4">
                    <Fish className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-[#D4AF37] text-sm uppercase tracking-wider font-body">New Experience</span>
                  </div>
                  <h2 className="font-heading text-3xl lg:text-5xl font-bold text-[#F5F5F0] mb-4">
                    The Royal Roll Room
                  </h2>
                  <p className="font-accent text-xl text-[#D4AF37] italic mb-4">Soulful Luxury Sushi Prepared Live On-Site</p>
                  <p className="font-body text-[#A1A1AA]">
                    A live sushi experience crafted with soulful flavor, cultural elegance, and luxury presentation. 
                    Chef prepares each roll and sashimi selection on-site at a dedicated sushi table for an unforgettable culinary moment.
                  </p>
                </div>

                {/* Signature Rolls */}
                <div className="mb-16">
                  <h3 className="font-heading text-2xl font-bold text-[#F5F5F0] mb-6 flex items-center gap-3">
                    <Star className="w-6 h-6 text-[#D4AF37]" />
                    Signature Rolls
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {signatureRolls.map((item, index) => (
                      <motion.div
                        key={index}
                        variants={fadeInUp}
                        className={`bg-[#121212] border border-white/5 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-all duration-500 ${item.featured ? 'ring-1 ring-[#D4AF37]/30' : ''}`}
                      >
                        <div className="flex justify-between items-start gap-4 mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-heading text-lg font-semibold text-[#F5F5F0]">{item.name}</h4>
                              {item.featured && <Star className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />}
                              {item.vegan && <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">V</span>}
                            </div>
                            <p className="font-accent text-sm text-[#D4AF37] italic">{item.subtitle}</p>
                          </div>
                          <span className="font-heading text-xl text-[#D4AF37]">${item.price}</span>
                        </div>
                        <p className="font-body text-sm text-[#A1A1AA]">{item.description}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Sashimi Selections */}
                <div className="mb-16">
                  <h3 className="font-heading text-2xl font-bold text-[#F5F5F0] mb-6 flex items-center gap-3">
                    <Fish className="w-6 h-6 text-[#D4AF37]" />
                    Sashimi Selections
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {sashimiSelections.map((item, index) => (
                      <motion.div
                        key={index}
                        variants={fadeInUp}
                        className={`bg-[#121212] border border-white/5 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-all duration-500 ${item.featured ? 'ring-1 ring-[#D4AF37]/30' : ''}`}
                      >
                        <div className="flex justify-between items-start gap-4 mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-heading text-lg font-semibold text-[#F5F5F0]">{item.name}</h4>
                              {item.featured && <Star className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />}
                            </div>
                            <p className="font-accent text-sm text-[#D4AF37] italic">{item.subtitle}</p>
                          </div>
                          <span className="font-heading text-xl text-[#D4AF37]">${item.price}</span>
                        </div>
                        <p className="font-body text-sm text-[#A1A1AA]">{item.description}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Sushi Add-Ons */}
                <div className="bg-[#121212] border border-[#D4AF37]/20 rounded-2xl p-8">
                  <h3 className="font-heading text-xl font-bold text-[#D4AF37] mb-6">Sushi Add-Ons</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sushiAddOns.map((item, index) => (
                      <div key={index} className="flex justify-between items-center py-3 border-b border-white/5 last:border-0">
                        <div>
                          <p className="font-body text-[#F5F5F0]">{item.name}</p>
                          <p className="font-body text-xs text-[#A1A1AA]">{item.description}</p>
                        </div>
                        <span className="font-heading text-lg text-[#D4AF37]">{item.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </TabsContent>

            {/* ==================== VEGAN TAB ==================== */}
            <TabsContent value="vegan">
              <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
                <div className="mb-10">
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold text-[#F5F5F0] flex items-center gap-3">
                    <Leaf className="w-8 h-8 text-[#D4AF37]" />
                    Vegan Royalty
                  </h2>
                  <p className="font-body text-[#A1A1AA] mt-2">Intentional, flavorful, elevated plant-based options</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {veganMenu.map((item, index) => (
                    <MenuItem key={index} item={item} index={index} showImage={item.image} />
                  ))}
                </div>
              </motion.div>
            </TabsContent>

            {/* ==================== KIDS TAB ==================== */}
            <TabsContent value="kids">
              <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
                <div className="mb-10">
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold text-[#F5F5F0] flex items-center gap-3">
                    <Baby className="w-8 h-8 text-[#D4AF37]" />
                    Kids Royal Menu
                  </h2>
                  <p className="font-body text-[#A1A1AA] mt-2">Ages 8–12 | Under 7 eat free</p>
                </div>
                <motion.div variants={fadeInUp} className="bg-[#121212] border border-white/5 rounded-2xl p-8 max-w-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-heading text-2xl font-bold text-[#F5F5F0]">Kids Royal Plate</h3>
                      <p className="font-body text-[#A1A1AA] mt-2">Choice of Chicken, Steak, or Shrimp with fried rice and vegetables</p>
                    </div>
                    <span className="font-heading text-3xl text-[#D4AF37]">$20</span>
                  </div>
                </motion.div>
              </motion.div>
            </TabsContent>

            {/* ==================== ADD-ONS TAB ==================== */}
            <TabsContent value="addons">
              <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
                {/* Starters & Sides */}
                <div className="mb-12">
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold text-[#F5F5F0] mb-6">Starters & Sides</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {startersAndSides.map((item, index) => (
                      <motion.div
                        key={index}
                        variants={fadeInUp}
                        className="bg-[#121212] border border-white/5 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-heading text-lg font-semibold text-[#F5F5F0]">{item.name}</h3>
                            <p className="font-body text-sm text-[#A1A1AA] mt-1">{item.description}</p>
                          </div>
                          <span className="font-heading text-xl text-[#D4AF37]">{item.price}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Event Add-Ons */}
                <div>
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold text-[#F5F5F0] mb-6">Event Enhancements</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    {eventAddOns.map((item, index) => (
                      <motion.div
                        key={index}
                        variants={fadeInUp}
                        className="bg-[#121212] border border-white/5 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-colors text-center"
                      >
                        <h3 className="font-heading text-lg font-semibold text-[#F5F5F0]">{item.name}</h3>
                        <p className="font-body text-sm text-[#A1A1AA] mt-1 mb-3">{item.description}</p>
                        <span className="font-heading text-2xl text-[#D4AF37]">{item.price}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* ==================== POLICIES ==================== */}
      <section className="py-24 lg:py-32 bg-[#121212]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="font-heading text-3xl font-bold text-[#F5F5F0]">Policies & Minimums</h2>
            </motion.div>
            <motion.div variants={fadeInUp} className="bg-[#050505] border border-white/5 rounded-2xl p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {policies.map((policy, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                    <span className="font-body text-[#A1A1AA]">{policy}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <motion.div 
          className="max-w-4xl mx-auto px-6 lg:px-8 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.h2 variants={fadeInUp} className="font-heading text-4xl sm:text-5xl font-bold text-[#F5F5F0] mb-6">
            Ready to Feast Like <span className="italic text-[#D4AF37]">Royalty</span>?
          </motion.h2>
          <motion.p variants={fadeInUp} className="font-body text-lg text-[#A1A1AA] mb-10">
            Reserve your date and let us bring the Royal Delights experience to you.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/booking">
              <Button 
                data-testid="menu-book-btn"
                className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-semibold rounded-full px-10 py-6 text-base transition-all duration-300 hover:scale-105 shadow-[0_0_30px_-10px_rgba(212,175,55,0.5)]"
              >
                Book Your Experience
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default MenuPage;
